import React, { useContext } from "react"

import { Form, Formik } from "formik"
import { NotificationManager } from "react-notifications"
import * as Yup from "yup"

import { surveyOptionsWithIcons } from "src/enums"

import { gql, useClient, useMutation, useQuery } from "hooks/urql"

import {
  RadioField,
  handleFailure,
} from "components/Forms/Formik/hookComponents"
import ModalWithProvidedBody, { Footer } from "components/ModalWithProvidedBody"
import UrqlLoadingIndicator from "components/WithLoadingIndicator/urql"

import CloudroomContext from "../CloudroomContext"

const ValidationSchema = Yup.object().shape({
  answers: Yup.array().of(
    Yup.object().shape({
      value: Yup.number().required(
        "Please select a response for each question"
      ),
    })
  ),
})

const SurveyModal: React.FC = () => {
  const { session, surveyOpen, setSurveyOpen } = useContext(CloudroomContext)

  const client = useClient()
  const [result] = useQuery({
    query: surveyQuery,
    variables: { sessionId: session.id },
    client,
  })
  const [, submitSurvey]: any[] = useMutation(submitSurveyMutation, client)
  const formState = {
    sessionId: session.id,
    answers: [],
  }

  if (result.data) {
    result.data.airtutorsSession.survey.questions.forEach((question, index) => {
      formState.answers[index] = { questionId: question.id }
    })
  }

  const handleSubmit = (values, actions) => {
    submitSurvey(values)
      .then(
        result => {
          const { failures } = result.data.submitSurvey
          if (failures.length > 0) {
            handleFailure(actions, failures)
          } else {
            NotificationManager.info(
              "Thank you for submitting your survey answers!"
            )
            setSurveyOpen(false)
          }
        },
        () => handleFailure(actions)
      )
      .catch(() => handleFailure(actions))
  }

  if (!session.survey || !surveyOpen) return null

  return (
    <ModalWithProvidedBody
      isOpen={surveyOpen}
      hideTrigger
      modalTitle="Student Survey"
      modalClassName="bootstrap-modal"
      closeModal={() => setSurveyOpen(false)}
    >
      <UrqlLoadingIndicator result={result}>
        {({ data: { airtutorsSession } }) => (
          <Formik
            initialValues={formState}
            onSubmit={handleSubmit}
            validationSchema={ValidationSchema}
            validateOnChange={false}
            enableReinitialize
          >
            <Form>
              <div className="modal-body">
                {airtutorsSession.survey.questions.map((question, index) => (
                  <div
                    key={question.id}
                    className="mb-4 border-0 border-b-2 border-solid border-b-slate-200"
                  >
                    <label className="mb-4 block text-xl">
                      {question.text}
                    </label>
                    <RadioField
                      name={`answers[${index}][value]`}
                      options={surveyOptionsWithIcons}
                      numeric
                    />
                  </div>
                ))}
              </div>

              <Footer
                cancelAction={() => setSurveyOpen(false)}
                submitText="Submit Survey"
                isCancelable={false}
              />
            </Form>
          </Formik>
        )}
      </UrqlLoadingIndicator>
    </ModalWithProvidedBody>
  )
}

const surveyQuery = gql`
  query SessionSurvey($sessionId: ID!) {
    airtutorsSession(id: $sessionId) {
      id
      survey {
        id
        questions {
          id
          text
        }
      }
    }
  }
`

const submitSurveyMutation = gql`
  mutation SubmitSurvey(
    $sessionId: ID!
    $answers: [SurveyAnswerInputObject!]!
  ) {
    submitSurvey(sessionId: $sessionId, answers: $answers) {
      failures {
        message
      }
    }
  }
`

export default SurveyModal
